/**
 * @fileoverview Candidacy Header Actions Component
 * 
 * Displays Approve and Reject action buttons in the header next to inter-page navigation
 */

import { useState } from "react";
import { Button } from "components/ui/button";
import { CheckCircle, XCircle, PaperPlaneTilt } from "@phosphor-icons/react";
import { useSession } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";

interface CandidacyHeaderActionsProps {
  entity: any;
  onRefresh?: () => void;
}

export const CandidacyHeaderActions: React.FC<CandidacyHeaderActionsProps> = ({ 
  entity, 
  onRefresh 
}) => {
  const { session } = useSession();
  const navigate = useNavigate();
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isSendingInvite, setIsSendingInvite] = useState(false);

  if (!entity) return null;

  const handleApprove = async () => {
    if (!entity?.id) return;

    if (!window.confirm('Weet je zeker dat je deze kandidaat wilt goedkeuren?')) {
      return;
    }

    setIsApproving(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/approve/${entity.id}`,
        { method: "PUT" },
        session
      );

      if (response.ok) {
        const result = await response.json();
        toast.success("Kandidaat goedgekeurd!");
        console.log("Application approved successfully", result);

        // If contacts were created, navigate to add contract
        if (result.contacts && result.contacts.length > 0) {
          const unitId = entity.unit?.id || entity.unitId;

          // Map all contacts with their type (tenant or co-inhabitant)
          // prefer the resourceEntityId returned by the API, fallback to contact id
          const allContacts = result.contacts.map((contact: any) => ({
            id: contact.resourceEntityId ?? contact.id,
            resourceEntityId: contact.resourceEntityId ?? null,
            name: `${contact.firstName} ${contact.lastName}`,
            isOnContract: contact.isOnContract,
            wasExisting: contact.wasExisting ?? false,
            residentData: contact.residentData ?? null,
          }));

          // Navigate to building detail with contract modal pre-filled
          // Including all contacts and total residents count
          navigate(`/building/${entity.buildingId}/verhuur`, {
            state: {
              allContacts,
              unitId,
              contractType: 'RESIDENTIAL_LONG_FLANDERS',
              totalResidents: result.totalResidents || result.contacts.length
            }
          });
        } else if (onRefresh) {
          onRefresh();
        }
      } else {
        toast.error("Fout bij goedkeuren kandidaat");
      }
    } catch (error) {
      console.error("Error approving application:", error);
      toast.error("Fout bij goedkeuren kandidaat");
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (!entity?.id) return;

    if (!window.confirm('Weet je zeker dat je deze kandidaat wilt afwijzen?')) {
      return;
    }

    setIsRejecting(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/reject/${entity.id}`,
        { method: "PUT" },
        session
      );

      if (response.ok) {
        toast.success("Kandidaat afgewezen");
        if (onRefresh) {
          onRefresh();
        }
      } else {
        toast.error("Fout bij afwijzen kandidaat");
      }
    } catch (error) {
      console.error("Error rejecting application:", error);
      toast.error("Fout bij afwijzen kandidaat");
    } finally {
      setIsRejecting(false);
    }
  };

  const handleSendInviteMail = async () => {
    if (!entity?.id) return;

    setIsSendingInvite(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/send-invite-mail/${entity.id}`,
        { method: "POST" },
        session
      );

      if (response.ok) {
        toast.success("Uitnodigingsmail verstuurd!");
        if (onRefresh) {
          onRefresh();
        }
      } else {
        toast.error("Fout bij versturen uitnodiging");
      }
    } catch (error) {
      console.error("Error sending invite mail:", error);
      toast.error("Fout bij versturen uitnodiging");
    } finally {
      setIsSendingInvite(false);
    }
  };

  // Determine which actions to show based on status
  const showApprove = entity.status !== 'approved' && entity.status !== 'rejected';
  const showReject = entity.status !== 'rejected';
  const showSendInvite = entity.status === 'notify' || entity.status === 'invited';

  // Don't show the section if no actions are available
  if (!showApprove && !showReject && !showSendInvite) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 h-full">
      {showApprove && (
        <Button
          onClick={handleApprove}
          disabled={isApproving}
          size="sm"
          className="bg-moss-primary text-white hover:bg-moss-primary/90 h-8"
        >
          <CheckCircle size={16} weight="duotone" className="mr-2" />
          {isApproving ? "Goedkeuren..." : "Goedkeuren"}
        </Button>
      )}

      {showReject && (
        <Button
          onClick={handleReject}
          disabled={isRejecting}
          size="sm"
          variant="destructive"
          className="h-8"
        >
          <XCircle size={16} weight="duotone" className="mr-2" />
          {isRejecting ? "Afwijzen..." : "Afwijzen"}
        </Button>
      )}

      {showSendInvite && (
        <Button
          onClick={handleSendInviteMail}
          disabled={isSendingInvite}
          size="sm"
          className="bg-blue-600 text-white hover:bg-blue-700 h-8"
        >
          <PaperPlaneTilt size={16} weight="duotone" className="mr-2" />
          {isSendingInvite ? "Versturen..." : "Uitnodiging versturen"}
        </Button>
      )}
    </div>
  );
};

